import { useState } from "react";

/**
 * A single resolved iteration in the Templated Block preview. Begins collapsed (real-world blocks can
 * have many iterations with 20+ substitutions each) and expands to show its key -> value pairs.
 */
function TemplatedBlockPreviewIteration({ index, substitutions }: { index: number, substitutions: { key: string, value: string }[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-green-200 rounded bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-1 text-xs font-medium text-green-700 bg-transparent border-0 cursor-pointer"
      >
        <span>{open ? '▾' : '▸'} Iteration {index + 1}</span>
        <span className="text-gray-500">{substitutions.length} substitution{substitutions.length === 1 ? '' : 's'}</span>
      </button>
      {open && (
        <div className="px-2 pb-2">
          {substitutions.length === 0 ? (
            <div className="text-xs text-gray-400 italic">No substitutions</div>
          ) : (
            <table className="text-xs">
              <tbody>
                {substitutions.map((s, j) => (
                  <tr key={j}>
                    <td className="pr-3 font-mono text-gray-600 align-top">{s.key}</td>
                    <td className="font-mono text-gray-900 break-all">{s.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renders the fully-resolved result of a Templated Block: one collapsible block per iteration, each
 * listing the key -> value substitutions that will be applied to the block's components for that
 * iteration. References (Template Iteration(s) / Template Substitution(s)) are expanded upstream.
 */
export function TemplatedBlockPreview({ iterations }: { iterations: { key: string, value: string }[][] }) {
  return (
    <div className="w-full p-3 bg-green-50 border border-green-200">
      <div className="text-xs font-semibold text-green-700 mb-2">
        Resolved result — {iterations.length} iteration{iterations.length === 1 ? '' : 's'}
      </div>
      {iterations.length === 0 ? (
        <div className="text-xs text-gray-500 italic">No iterations resolved.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {iterations.map((substitutions, i) => (
            <TemplatedBlockPreviewIteration key={i} index={i} substitutions={substitutions} />
          ))}
        </div>
      )}
    </div>
  );
}